import { useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import {
    UserCircle,
    Download,
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from "lucide-react";

const AdvisorsSection = ({ advisors }) => {
    const columns = useMemo(() => [
        {
            Header: 'ID',
            accessor: 'advisorId',
            Cell: ({ value }) => (
                <span className="text-gray-900 font-medium">{value}</span>
            ),
        },
        {
            Header: 'Last Name',
            accessor: 'lastName',
            Cell: ({ value }) => (
                <span className="text-gray-700">{value}</span>
            ),
        },
        {
            Header: 'First Name',
            accessor: 'firstName',
            Cell: ({ value }) => (
                <span className="text-gray-700">{value}</span>
            ),
        },
        {
            Header: 'Email',
            accessor: 'email',
            Cell: ({ value }) => (
                <span className="text-gray-700">{value}</span>
            ),
        },
        {
            Header: 'Description',
            accessor: 'description',
            Cell: ({ value }) => (
                <span className="text-gray-500">{value || '-'}</span>
            ),
        },
    ], []);

    const data = useMemo(() => advisors.filter(a => a.advisorId), [advisors]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        rows,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 10 },
        },
        useSortBy,
        usePagination
    );

    const downloadCSV = () => {
        const headers = ["ID", "Last Name", "First Name", "Email", "Description"];
        let csvContent = headers.join(",") + "\n";

        rows.forEach(row => {
            const advisor = row.original;
            csvContent += `"${advisor.advisorId || ''}","${advisor.lastName || ''}","${advisor.firstName || ''}","${advisor.email || ''}","${advisor.description || ''}"\n`;
        });

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", "Advisors.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="flex-1 px-6 md:px-12 lg:pl-12 lg:pr-24 py-6 pb-5 overflow-y-auto">
            {/* page header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-xl">
                    <UserCircle className="w-6 h-6 text-primary" />
                </div>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Advisors</h1>
                    <p className="text-sm text-gray-500">View your advisor network</p>
                </div>
            </div>

            {/* table card */}
            <div className="bg-white rounded-2xl shadow-card p-6">
                {data.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <UserCircle className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500">No advisors found</p>
                    </div>
                ) : (
                    <>
                        {/* header */}
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                                Advisor List
                            </h3>
                            <button
                                onClick={downloadCSV}
                                disabled={rows.length === 0}
                                className="h-9 px-4 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Download CSV"
                            >
                                <Download className="w-4 h-4" />
                                <span>CSV</span>
                            </button>
                        </div>

                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table {...getTableProps()} className="w-full text-sm">
                                <thead>
                                    {headerGroups.map(headerGroup => (
                                        <tr {...headerGroup.getHeaderGroupProps()} className="border-b border-gray-100">
                                            {headerGroup.headers.map(column => (
                                                <th
                                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                                    className="text-left py-3 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 transition-colors"
                                                >
                                                    <span className="inline-flex items-center gap-1">
                                                        {column.render('Header')}
                                                        {column.isSorted && (
                                                            column.isSortedDesc
                                                                ? <ChevronDown className="w-3 h-3" />
                                                                : <ChevronUp className="w-3 h-3" />
                                                        )}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody {...getTableBodyProps()} className="divide-y divide-gray-50">
                                    {page.map(row => {
                                        prepareRow(row);
                                        return (
                                            <tr {...row.getRowProps()} className="hover:bg-gray-50 transition-colors">
                                                {row.cells.map(cell => (
                                                    <td {...cell.getCellProps()} className="py-3 px-3">
                                                        {cell.render('Cell')}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile cards */}
                        <div className="md:hidden space-y-3">
                            {page.map(row => {
                                prepareRow(row);
                                const advisor = row.original;
                                return (
                                    <div key={advisor.advisorId} className="bg-gray-50 rounded-xl p-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                                <UserCircle className="w-5 h-5 text-primary" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h4 className="font-semibold text-gray-900">
                                                        {advisor.firstName} {advisor.lastName}
                                                    </h4>
                                                    <span className="text-xs text-gray-400">#{advisor.advisorId}</span>
                                                </div>
                                                <p className="text-sm text-gray-600 truncate">{advisor.email}</p>
                                                {advisor.description && (
                                                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{advisor.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* pagination */}
                        {data.length > 10 && (
                            <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => gotoPage(0)}
                                        disabled={!canPreviousPage}
                                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="First page"
                                    >
                                        <ChevronsLeft className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={previousPage}
                                        disabled={!canPreviousPage}
                                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Previous page"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={nextPage}
                                        disabled={!canNextPage}
                                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Next page"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => gotoPage(pageOptions.length - 1)}
                                        disabled={!canNextPage}
                                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Last page"
                                    >
                                        <ChevronsRight className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <span>
                                        Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                                    </span>
                                    <span className="flex items-center gap-2">
                                        Go to:
                                        <input
                                            type="number"
                                            min={1}
                                            max={pageOptions.length}
                                            defaultValue={pageIndex + 1}
                                            onChange={(e) => {
                                                const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                                                gotoPage(pageNumber);
                                            }}
                                            className="w-14 px-2 py-1 border border-gray-200 rounded-lg text-center focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                        />
                                    </span>
                                </div>

                                <select
                                    value={pageSize}
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                >
                                    {[10, 20, 30, 40].map((size) => (
                                        <option key={size} value={size}>
                                            Show {size}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AdvisorsSection;
